import { useEffect, useMemo, useRef } from 'react'

import { useLightMode } from '@/hooks/useLightMode'
import { useTranslation } from 'react-i18next'

import { useToken } from '@chakra-ui/react'

import { tVar } from '@/utils/i18n'

import { TailgateGeometrySidecarI } from '../types/tailgateGeometrySidecar'
import {
  getContainedContentRect,
  getCumulativeZoneCounts,
  getTailgateFrameIndex,
} from '../utils/overlayGeometry'

interface TailgateOverlayLayersI {
  boxes: boolean
  counts: boolean
  trackIds: boolean
  zones: boolean
}

interface TailgateGridTestOverlayIProps {
  layers: TailgateOverlayLayersI
  onUnsupported: () => void
  sidecar: TailgateGeometrySidecarI
  videoElement: HTMLVideoElement | null
}

interface VideoFrameMetadataI {
  mediaTime: number
}

type VideoWithFrameCallbacks = HTMLVideoElement & {
  cancelVideoFrameCallback?: (handle: number) => void
  requestVideoFrameCallback?: (
    callback: (now: number, metadata: VideoFrameMetadataI) => void
  ) => number
}

const drawPolygon = (
  context: CanvasRenderingContext2D,
  points: Array<[number, number]>,
  mapX: (value: number) => number,
  mapY: (value: number) => number,
  fillStyle: string,
  strokeStyle: string
) => {
  const first = points[0]
  if (!first) return

  context.beginPath()
  context.moveTo(mapX(first[0]), mapY(first[1]))
  points.slice(1).forEach(([x, y]) => context.lineTo(mapX(x), mapY(y)))
  context.closePath()
  context.fillStyle = fillStyle
  context.fill()
  context.lineWidth = 2
  context.strokeStyle = strokeStyle
  context.stroke()
}

/**
 * Names the zone with centered text at its polygon's centroid, in the zone's
 * own color — the same treatment ScalableGridOverlay gives the grid preview,
 * so the sidecar render reads identically to the GTZ preview clip (ENG-34102).
 */
const drawZoneLabel = (
  context: CanvasRenderingContext2D,
  points: Array<[number, number]>,
  label: string,
  mapX: (value: number) => number,
  mapY: (value: number) => number,
  fontSize: number,
  fillStyle: string
) => {
  if (points.length === 0) return

  const centroidX = points.reduce((sum, [x]) => sum + x, 0) / points.length
  const centroidY = points.reduce((sum, [, y]) => sum + y, 0) / points.length

  context.save()
  context.font = `bold ${fontSize}px sans-serif`
  context.textAlign = 'center'
  context.fillStyle = fillStyle
  context.fillText(label, mapX(centroidX), mapY(centroidY))
  context.restore()
}

export const TailgateGridTestOverlay = ({
  layers,
  onUnsupported,
  sidecar,
  videoElement,
}: TailgateGridTestOverlayIProps) => {
  const { t } = useTranslation()
  const mode = useLightMode()
  const [
    labelBackground,
    labelText,
    neutralTrack,
    securedFill,
    securedStroke,
    securedTrack,
    unsecuredFill,
    unsecuredStroke,
    unsecuredTrack,
  ] = useToken('colors', [
    `tailgate-overlay.label-background.${mode}`,
    `tailgate-overlay.label-text.${mode}`,
    `tailgate-overlay.neutral-track.${mode}`,
    `tailgate-overlay.secured.fill.${mode}`,
    `tailgate-overlay.secured.stroke.${mode}`,
    `tailgate-overlay.secured.track.${mode}`,
    `tailgate-overlay.unsecured.fill.${mode}`,
    `tailgate-overlay.unsecured.stroke.${mode}`,
    `tailgate-overlay.unsecured.track.${mode}`,
  ])
  const zoneColors = useMemo(
    () => ({
      labelBackground,
      labelText,
      neutralTrack,
      secured: {
        fill: securedFill,
        stroke: securedStroke,
        track: securedTrack,
      },
      unsecured: {
        fill: unsecuredFill,
        stroke: unsecuredStroke,
        track: unsecuredTrack,
      },
    }),
    [
      labelBackground,
      labelText,
      neutralTrack,
      securedFill,
      securedStroke,
      securedTrack,
      unsecuredFill,
      unsecuredStroke,
      unsecuredTrack,
    ]
  )
  // Precomputed once per sidecar: the counts chip shows how many unique
  // tracks have entered each zone so far (churn-filtered — see
  // getCumulativeZoneCounts), not the current frame's occupancy.
  const cumulativeZoneCounts = useMemo(
    () => getCumulativeZoneCounts(sidecar.frames, sidecar.crossings),
    [sidecar]
  )
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const lastFrameIndexRef = useRef(0)
  const layersRef = useRef(layers)
  const redrawRef = useRef<(() => void) | null>(null)
  layersRef.current = layers

  useEffect(() => {
    redrawRef.current?.()
  }, [layers])

  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas || !videoElement) return

    const drawFrame = (frameIndex: number) => {
      const context = canvas.getContext('2d')
      if (!context) return

      const bounds = canvas.getBoundingClientRect()
      const pixelRatio = window.devicePixelRatio || 1
      const nextWidth = Math.max(1, Math.round(bounds.width * pixelRatio))
      const nextHeight = Math.max(1, Math.round(bounds.height * pixelRatio))
      if (canvas.width !== nextWidth || canvas.height !== nextHeight) {
        canvas.width = nextWidth
        canvas.height = nextHeight
      }

      context.setTransform(pixelRatio, 0, 0, pixelRatio, 0, 0)
      context.clearRect(0, 0, bounds.width, bounds.height)

      const contentRect = getContainedContentRect(
        bounds.width,
        bounds.height,
        sidecar.frame.w,
        sidecar.frame.h
      )
      if (!contentRect) return

      const mapX = (value: number) =>
        contentRect.offsetX + value * contentRect.scale
      const mapY = (value: number) =>
        contentRect.offsetY + value * contentRect.scale
      const tracks = sidecar.frames[frameIndex] ?? []
      const activeLayers = layersRef.current

      if (activeLayers.zones && sidecar.scaling_mode !== 'none') {
        drawPolygon(
          context,
          sidecar.zones.unsecured,
          mapX,
          mapY,
          zoneColors.unsecured.fill,
          zoneColors.unsecured.stroke
        )
        drawPolygon(
          context,
          sidecar.zones.secured,
          mapX,
          mapY,
          zoneColors.secured.fill,
          zoneColors.secured.stroke
        )
        // Matches ScalableGridOverlay's label sizing: 4% of the frame height.
        const zoneLabelFontSize = Math.round(contentRect.height * 0.04)
        drawZoneLabel(
          context,
          sidecar.zones.unsecured,
          t('tailgate:grid_test_v2.zone_label_unsecured'),
          mapX,
          mapY,
          zoneLabelFontSize,
          zoneColors.unsecured.stroke
        )
        drawZoneLabel(
          context,
          sidecar.zones.secured,
          t('tailgate:grid_test_v2.zone_label_secured'),
          mapX,
          mapY,
          zoneLabelFontSize,
          zoneColors.secured.stroke
        )
      }

      if (activeLayers.boxes || activeLayers.trackIds) {
        tracks.forEach((track) => {
          const [x1, y1, x2, y2] = track.box
          const left = mapX(x1)
          const top = mapY(y1)
          const width = (x2 - x1) * contentRect.scale
          const height = (y2 - y1) * contentRect.scale
          const trackColor = track.in_secured
            ? zoneColors.secured.track
            : track.in_unsecured
            ? zoneColors.unsecured.track
            : zoneColors.neutralTrack

          if (activeLayers.boxes) {
            context.lineWidth = 2
            context.strokeStyle = trackColor
            context.strokeRect(left, top, width, height)
          }

          if (activeLayers.trackIds) {
            const label = `#${track.id}`
            context.font = '600 12px sans-serif'
            const labelWidth = context.measureText(label).width + 8
            context.fillStyle = zoneColors.labelBackground
            context.fillRect(
              left,
              Math.max(contentRect.offsetY, top - 18),
              labelWidth,
              18
            )
            context.fillStyle = zoneColors.labelText
            context.fillText(
              label,
              left + 4,
              Math.max(contentRect.offsetY + 13, top - 5)
            )
          }
        })
      }

      if (activeLayers.counts) {
        // Cumulative unique-track counts up to this frame — the chip only
        // ever increments as the clip plays (ENG-34102).
        const zoneCounts = cumulativeZoneCounts[frameIndex] ?? {
          secured: 0,
          unsecured: 0,
        }
        const label = tVar(t, 'tailgate:grid_test_v2.counts_chip', {
          badgeCount: sidecar.verdict.badge_count,
          securedCount: zoneCounts.secured,
          unsecuredCount: zoneCounts.unsecured,
        })
        context.font = '600 12px sans-serif'
        const chipWidth = context.measureText(label).width + 16
        const chipX = contentRect.offsetX + contentRect.width - chipWidth - 8
        const chipY = contentRect.offsetY + 8
        context.fillStyle = zoneColors.labelBackground
        context.fillRect(chipX, chipY, chipWidth, 26)
        context.fillStyle = zoneColors.labelText
        context.fillText(label, chipX + 8, chipY + 17)
      }
    }

    const video = videoElement as VideoWithFrameCallbacks
    if (!video.requestVideoFrameCallback) {
      onUnsupported()
      return
    }

    redrawRef.current = () => drawFrame(lastFrameIndexRef.current)

    let isActive = true
    let callbackHandle: number | null = null
    const handleVideoFrame = (_now: number, metadata: VideoFrameMetadataI) => {
      if (!isActive) return
      const frameIndex = getTailgateFrameIndex(
        metadata.mediaTime,
        sidecar.fps,
        sidecar.frame_count
      )
      if (frameIndex !== null) {
        lastFrameIndexRef.current = frameIndex
        drawFrame(frameIndex)
      }
      callbackHandle =
        video.requestVideoFrameCallback?.(handleVideoFrame) ?? null
    }

    callbackHandle = video.requestVideoFrameCallback(handleVideoFrame)

    const resizeObserver =
      typeof ResizeObserver === 'undefined'
        ? null
        : new ResizeObserver(() => drawFrame(lastFrameIndexRef.current))
    resizeObserver?.observe(canvas)
    drawFrame(lastFrameIndexRef.current)

    return () => {
      isActive = false
      redrawRef.current = null
      resizeObserver?.disconnect()
      if (callbackHandle !== null) {
        video.cancelVideoFrameCallback?.(callbackHandle)
      }
    }
  }, [
    cumulativeZoneCounts,
    onUnsupported,
    sidecar,
    t,
    videoElement,
    zoneColors,
  ])

  return (
    <canvas
      aria-hidden='true'
      data-testid='tailgate-grid-test-overlay'
      ref={canvasRef}
      style={{
        height: '100%',
        inset: 0,
        pointerEvents: 'none',
        position: 'absolute',
        width: '100%',
        zIndex: 1,
      }}
    />
  )
}
